import { FilterOperatorUi } from "@common/api/filterTypes.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { DateFieldValue, getDateRangeFromValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu.tsx";
import { NumberIntervalValue } from "@common/components/filterPopover/subMenues/NumberFieldSubMenu.tsx";

type ListFilterMapping<T> = {
    id: string;
    candidate: (row: T) => string | Array<string> | null | undefined;
};

type TextFilterMapping<T> = {
    id: string;
    type: "text";
    candidate: (row: T) => string | null | undefined;
};

type FilterMapping<T> = ListFilterMapping<T> | TextFilterMapping<T>;

export function applyClientFilters<T>(
    rows: T[] | undefined,
    mapping: FilterMapping<T>[],
    selectFilters: Record<string, Array<string>>,
    textFilters: Record<string, string>,
    filterOperators: Record<string, FilterOperatorUi>,
    customPredicate?: (row: T) => boolean
): T[] | undefined {
    if (!rows) return undefined;

    return rows.filter((row) => {
        for (const entry of mapping) {
            if ("type" in entry && entry.type === "text") {
                if (!matchesTextFilter(textFilters[entry.id], filterOperators[entry.id], entry.candidate(row))) return false;
            } else {
                const listEntry = entry as ListFilterMapping<T>;
                if (!matchesListFilter(selectFilters[listEntry.id], filterOperators[listEntry.id], listEntry.candidate(row))) return false;
            }
        }
        return customPredicate ? customPredicate(row) : true;
    });
}

export function withOperators(options: FilterOption[], operators: Record<string, FilterOperatorUi>): FilterOption[] {
    return options.map((o) => (operators[o.id] !== undefined ? { ...o, filterOperator: operators[o.id] } : o));
}

export function matchesListFilter(
    selectedIds: Array<string> | undefined,
    operator: FilterOperatorUi | undefined,
    candidate: string | Array<string> | null | undefined
): boolean {
    if (!selectedIds || selectedIds.length === 0) return true;

    const isNegative = operator === FilterOperatorUi.Negative;

    if (Array.isArray(candidate)) {
        const hasOverlap = candidate.some((c) => selectedIds.includes(c));
        return isNegative ? !hasOverlap : hasOverlap;
    }

    if (candidate == null) {
        // Positive: row without a value cannot match a specific selection → exclude.
        // Negative: row without a value is not in the selected set → keep (matches SQL "NOT IN @p OR col IS NULL").
        return isNegative;
    }

    const contains = selectedIds.includes(candidate);
    return isNegative ? !contains : contains;
}

export function matchesTextFilter(query: string | null | undefined, operator: FilterOperatorUi | undefined, candidate: string | null | undefined): boolean {
    if (!query) return true;

    const haystack = (candidate ?? "").toLowerCase();
    const needle = query.toLowerCase();
    const contains = haystack.includes(needle);
    return operator === FilterOperatorUi.Negative ? !contains : contains;
}

export function hasDateRange(dateFilter: DateFieldValue | null | undefined): boolean {
    if (!dateFilter) return false;

    const hasDateFieldEnum = dateFilter.dateFieldEnum !== undefined && dateFilter.dateFieldEnum !== null;
    return !!dateFilter.from || !!dateFilter.to || hasDateFieldEnum || !!dateFilter.rollingPeriod;
}

export function matchesDateFilter(dateFilter: DateFieldValue | null | undefined, candidate: Date | string | null | undefined): boolean {
    if (!dateFilter) return true;

    if (!hasDateRange(dateFilter)) return true;

    const range = getDateRangeFromValue(dateFilter);
    if (!range) return !candidate;
    const { from: fromDate, to: toDate } = range;

    if (!candidate) return false;

    const candidateDateOnly = toDateOnly(new Date(candidate));

    if (fromDate && candidateDateOnly < toDateOnly(fromDate)) return false;
    if (toDate && toDateOnly(toDate) < candidateDateOnly) return false;

    return true;
}

function toDateOnly(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Inclusive numeric range match. A row without a value (null candidate) never falls inside a bounded range,
// so it is excluded once any bound is set, mirroring how a "field between X and Y" SQL predicate drops NULLs.
export function matchesNumberFilter(numberFilter: NumberIntervalValue | null | undefined, candidate: number | null | undefined): boolean {
    if (!numberFilter || (numberFilter.from === undefined && numberFilter.to === undefined)) return true;
    if (candidate == null) return false;
    if (numberFilter.from !== undefined && candidate < numberFilter.from) return false;
    if (numberFilter.to !== undefined && candidate > numberFilter.to) return false;
    return true;
}
